import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Box, Grid, LinearProgress } from '@mui/material';
import { LayoutDashboard } from '../../../shared/layouts';
import { GenericService } from '../../../shared/services';
import { AccessRoleDTO, AccessRoleFilterDTO, CompanyDTO, CompanyFilterDTO, PeopleDTO, PeopleFilterDTO, UserDTO, ValueListDTO } from '../../../shared/interfaces';
import { ISnackbarAlert, SnackbarAlert, CardList, TextFieldInput, ToolBar, IDialogActions, ICardActions, ICardColumns, IChipFilter, SelectField, IChipsSettingProps } from '../../../shared/components';
import { PeopleMaintenance } from './PeopleMaintenance';
import { Delete } from '../../../shared/components/confirm-delete/ConfirmDelete';
import { ListId, LocalStorange } from '../../../shared/environment';
import { useTranslation } from 'react-i18next';
import { CompanyPeopleAssociate } from './CompanyPeopleAssociate';

interface IPeople {
    data: any;
}

export const People: React.FC<IPeople> = ({ data }) => {

    const [typeList, setTypeList] = useState<ValueListDTO[]>([]);
    const [statusList, setStatusList] = useState<ValueListDTO[]>([]);
    const [jobTitleList, setJobTitleList] = useState<ValueListDTO[]>([]);
    const [companyList, setCompanyList] = useState<CompanyDTO[]>([]);
    const [businessRelationshipTypeList, setBusinessRelationshipTypeList] = useState<ValueListDTO[]>([]);
    const [ownerCompanyList, setOwnerCompanyList] = useState<CompanyDTO[]>([]);
    const [peopleList, setPeopleList] = useState<PeopleDTO[]>([]);
    const [peopleFilterDTO, setPeopleFilterDTO] = useState<PeopleFilterDTO>();

    const [userDTO, setUserDTO] = useState<UserDTO>();

    const [actionExecuted, setActionExecuted] = useState(false);
    const [newRecord, setNewRecord] = useState('');
    const [editRecord, setEditRecord] = useState('');
    const [deleteRecord, setDeleteRecord] = useState('');
    const [isOpenMaintenance, setIsOpenMaintenance] = useState(false);
    const [selectRecord, setSelectRecord] = useState('');

    const [loading, setLoading] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState<ISnackbarAlert>();
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);

    const [accessRolePermissionDTO, setAccessRolePermissionDTO] = useState<AccessRoleDTO>();
    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');
    const { t } = useTranslation();

    useEffect(() => {
        setPeopleFilterDTO({
            ...peopleFilterDTO,
            name: '',
            fullName: '',
            typeId: '',
            statusId: '',
            jobTitleId: '',
            companyId: data.id,
            ownerCompanyId: '',
            businessRelationShipTypeId: ''
        } as PeopleFilterDTO);
        setUserDTO({
            ...userDTO,
            id: '',
            userName: '',
            password: '',
            peopleId: '',
            isFirstAccess: true,
            isActive: true,
            lastConnectedIp: '',
            accessDeadlineDateTime: new Date(new Date().setMonth(new Date().getMonth() + 3)),
            isAutomaticRenovation: true,
            companyId: '',
        } as UserDTO);
    }, []);

    useEffect(() => {
        handlePeopleGetAllViewAsync();
        handleCompanyGetAll();
        handleOwnerCompanyGetAll();
        handleListGetAll(ListId.corpsphere.people.typeId, setTypeList);
        handleListGetAll(ListId.corpsphere.people.statusId, setStatusList);
        handleListGetAll(ListId.corpsphere.people.jobTitle, setJobTitleList);
        handleListGetAll(ListId.corpsphere.company.businessRelationshipTypeId, setBusinessRelationshipTypeList);

        handleGetPermission(ListId.corpsphere.pleopleId, setAccessRolePermissionDTO);
    }, [peopleFilterDTO]);

    useEffect(() => {
        if (actionExecuted)
            handlePeopleGetAllViewAsync();
    }, [actionExecuted]);

    const handleGetPermission = useCallback(async (featureId: string, setList: any) => {
        const accessRoleFilterDTO: AccessRoleFilterDTO = {
            featureId: featureId,
            userId: userInfo.user.id,
        };
        GenericService.PostGenericAsync1('authsphere.AccessRole', 'GetPermissionAsync', accessRoleFilterDTO, setActionExecuted, setLoading, undefined, setList);
    }, []);

    const handleCompanyGetAll = useCallback(async () => {
        const companyFIlterDTO: CompanyFilterDTO = {
            name: '',
            businessRelationshipTypeId: '',
            statusId: '',
            orderBy: 'name ASC'
        };
        GenericService.GetAllAsync1('corpsphere.company', companyFIlterDTO, setLoading, setCompanyList, showMessage);
    }, []);

    const handleOwnerCompanyGetAll = useCallback(async () => {
        const companyFIlterDTO: CompanyFilterDTO = {
            name: '',
            businessRelationshipTypeId: '',
            statusId: '',
            ownerCompanyId: '',
            orderBy: 'name ASC'
        };
        GenericService.GetAllAsync1('corpsphere.company', companyFIlterDTO, setLoading, setOwnerCompanyList, showMessage);
    }, []);

    const handleListGetAll = useCallback(async (listId: string, setList: any) => {
        const valueListFilterDTO = {
            value: '',
            listId: listId,
            orderBy: 'value ASC'
        };
        GenericService.GetAllAsync1(
            'parmsphere.valueList',
            valueListFilterDTO,
            setLoading,
            setList,
            showMessage
        );
    }, []);

    const handlePeopleGetAllViewAsync = useCallback(async () => {
        if (peopleFilterDTO === undefined)
            return;
        if (accessRolePermissionDTO?.allowGetAllView == undefined)
            return;
        if (!accessRolePermissionDTO?.allowGetAllView) {
            showMessage(t('message.allowGetAllView'), 'info');
            return;
        }
        GenericService.GetAllViewAsync1('corpsphere.people', peopleFilterDTO, setActionExecuted, setLoading, setPeopleList, showMessage, true);
    }, [peopleFilterDTO, accessRolePermissionDTO]);

    const handleClearFilterClick = () => {
        setPeopleFilterDTO({
            ...peopleFilterDTO,
            name: '',
            fullName: '',
            typeId: '',
            statusId: '',
            jobTitleId: '',
            companyId: '',
            ownerCompanyId: '',
            businessRelationShipTypeId: ''
        } as PeopleFilterDTO);
    };

    const handleCreateAccessUser = (data: any) => {
        const dto = userDTO as UserDTO;
        dto.userName = data.email;
        dto.peopleId = data.id;
        dto.createdById = userInfo.user.id;
        dto.password = data.id.substring(0, 8);
        dto.companyId = data.companyId;

        GenericService.AddAsync1('authsphere.user', dto, setUserDTO, setActionExecuted, setLoading, showMessage);
    };

    function handleSearchClick() {
        handlePeopleGetAllViewAsync();
    }

    const handleNew = () => {
        if (!accessRolePermissionDTO?.allowAdd) {
            showMessage(t('message.allowAdd'), 'info');
            return;
        }
        setNewRecord('data');
        setEditRecord('');
        setDeleteRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleViewDetatils = (data: string) => {
        if (!accessRolePermissionDTO?.allowGetById) {
            showMessage(t('message.allowGetById'), 'info');
            return;
        }
        setNewRecord('');
        setEditRecord(data);
        setDeleteRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleDelete = (data: string) => {
        if (!accessRolePermissionDTO?.allowDelete) {
            showMessage(t('message.allowDelete'), 'info');
            return;
        }
        setNewRecord('');
        setDeleteRecord(data);
        setEditRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleAssociateCompany = (data: string) => {
        setNewRecord('');
        setDeleteRecord('');
        setEditRecord('');
        setSelectRecord(data);
        setIsOpenMaintenance(true);
    };

    const toolBarActions: IDialogActions[] = [
        { id: 'new', text: t('people.button.new'), icon: 'add_circle_outlined', iconColor: 'primary', setAction: handleNew },
    ];

    const cardActions: ICardActions[] = [
        { id: 'viewDetails', toolTip: t('company.cardActions.viewDetails'), icon: 'preview_outlined', iconColor: 'primary', setAction: handleViewDetatils },
        { id: 'createUser', toolTip: t('people.cardActions.userAccessCreate'), icon: 'person_add_outlined', iconColor: 'success', setAction: handleCreateAccessUser },
        { id: 'associateUsers', toolTip: t('people.cardActions.associateCompanies'), icon: 'domain_outlined', iconColor: 'info', setAction: handleAssociateCompany },
        { id: 'delete', toolTip: t('company.cardActions.delete'), icon: 'delete_outlined', iconColor: 'error', setAction: handleDelete },
    ];

    const chipFilters: IChipFilter[] = [
        { label: t('people.chipFilters.totalRecords'), value: peopleList.length },
        { label: t('people.chipFilters.name'), value: peopleFilterDTO?.name },
        { label: t('people.chipFilters.fullName'), value: peopleFilterDTO?.fullName },
        { label: t('people.chipFilters.type'), value: typeList.find(x => x.id === peopleFilterDTO?.typeId)?.value },
        { label: t('people.chipFilters.status'), value: jobTitleList.find(x => x.id === peopleFilterDTO?.statusId)?.value },
        { label: t('people.chipFilters.jobTitle'), value: jobTitleList.find(x => x.id === peopleFilterDTO?.jobTitleId)?.value },
        { label: t('people.chipFilters.company'), value: companyList.find(x => x.id === peopleFilterDTO?.companyId)?.name },

    ];

    const chipStatus: IChipsSettingProps[] = [
        { value: t('people.chipStatus.active'), iconName: 'task_alt_outlined', iconColor: 'success' },
        { value: t('people.chipStatus.inactive'), iconName: 'block_outlined', iconColor: 'warning' },
    ];

    const chiphasUser: IChipsSettingProps[] = [
        { value: t('people.chiphasUser.true'), iconName: 'task_alt_outlined', iconColor: 'success' },
        { value: t('people.chiphasUser.false'), iconName: 'block_outlined', iconColor: 'warning' },
    ];

    const columns: ICardColumns[] = [
        { field: 'name', label: t('people.columns.name'), size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'fullName', label: t('people.columns.fullName'), size: 3, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'department', label: t('people.columns.department'), size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'jobTitle', label: t('people.columns.jobTitle'), size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'type', label: t('people.columns.type'), size: 3, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'phone', label: t('people.columns.phone'), size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'mobile', label: t('people.columns.mobile'), size: 3, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'email', label: t('people.columns.email'), size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'status', label: t('people.columns.status'), size: 2, type: 'text', visible: true, isChip: true, chipSettings: chipStatus, hasFieldLine: false, hasToolTip: true },
        { field: 'hasUser', label: t('people.columns.hasUser'), size: 2, type: 'text', visible: true, isChip: true, chipSettings: chiphasUser, hasFieldLine: false, hasToolTip: true },
    ];

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarMessage(msg);
        setIsOpenSnackBar(true);
    }

    return (
        <React.Fragment>
            {isOpenSnackBar && (
                <SnackbarAlert
                    snackBarAlert={snackBarMessage}
                    setIsOpen={setIsOpenSnackBar}
                    isOpen={isOpenSnackBar}
                />
            )}
            <LayoutDashboard title={t('people.listName')}>
                <ToolBar
                    clickSearch={handleSearchClick}
                    clickClearFilter={handleClearFilterClick}
                    chipFilters={chipFilters}
                    actions={toolBarActions}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={3}>
                            <TextFieldInput
                                id='name'
                                label={t('people.filter.name')}
                                setValue={setPeopleFilterDTO}
                                value={peopleFilterDTO?.name}
                                obj={peopleFilterDTO}
                                placeholder={t('people.filter.name')}
                                maxLength={200}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextFieldInput
                                id='fullName'
                                label={t('people.filter.fullName')}
                                setValue={setPeopleFilterDTO}
                                value={peopleFilterDTO?.fullName}
                                obj={peopleFilterDTO}
                                placeholder={t('people.filter.fullName')}
                                maxLength={200}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='typeId'
                                label={t('people.filter.type')}
                                setValue={setPeopleFilterDTO}
                                value={peopleFilterDTO?.typeId}
                                obj={peopleFilterDTO}
                                valueList={typeList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='statusId'
                                label={t('people.filter.status')}
                                setValue={setPeopleFilterDTO}
                                value={peopleFilterDTO?.statusId}
                                obj={peopleFilterDTO}
                                valueList={statusList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='jobTitleId'
                                label={t('people.filter.jobTitle')}
                                setValue={setPeopleFilterDTO}
                                value={peopleFilterDTO?.jobTitleId}
                                obj={peopleFilterDTO}
                                valueList={jobTitleList}
                                hasNoneSelect={true}
                                searchable={true}
                                labelKey='value'
                                valueKey='id'
                            />
                        </Grid>
                        {!data && (
                            <React.Fragment>
                                <Grid item xs={12} md={3}>
                                    <SelectField
                                        id='companyId'
                                        label={t('people.filter.company')}
                                        setValue={setPeopleFilterDTO}
                                        value={peopleFilterDTO?.companyId}
                                        obj={peopleFilterDTO}
                                        valueList={companyList}
                                        hasNoneSelect={true}
                                        searchable={true}
                                        labelKey='name'
                                        valueKey='id'
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <SelectField
                                        id='businessRelationShipTypeId'
                                        label={t('people.filter.businessRelationShipType')}
                                        setValue={setPeopleFilterDTO}
                                        value={peopleFilterDTO?.businessRelationShipTypeId}
                                        obj={peopleFilterDTO}
                                        valueList={businessRelationshipTypeList}
                                        hasNoneSelect={true}
                                        searchable={true}
                                        labelKey='value'
                                        valueKey='id'
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <SelectField
                                        id='ownerCompanyId'
                                        label={t('people.filter.ownerCompany')}
                                        setValue={setPeopleFilterDTO}
                                        value={peopleFilterDTO?.ownerCompanyId}
                                        obj={peopleFilterDTO}
                                        valueList={ownerCompanyList}
                                        hasNoneSelect={true}
                                        searchable={true}
                                        labelKey='name'
                                        valueKey='id'
                                    />
                                </Grid>
                            </React.Fragment>
                        )}
                    </Grid>
                </ToolBar>
                {loading && (
                    <LinearProgress sx={{ borderRadius: 3, mt: - 0.7, mb: 0.2 }} />
                )}
                <Box sx={{ display: 'flex', maxHeight: '80vh', width: '100%', overflow: 'hidden', overflowY: 'auto' }}>
                    <CardList
                        columns={columns}
                        rows={peopleList}
                        actions={cardActions}
                        hasChildren={false}
                        setRowExpanded={() => void undefined}
                        cardSize={12}
                    />
                </Box>
                {newRecord && (
                    <PeopleMaintenance
                        data={''}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                        parentId={''}
                    />
                )}
                {editRecord && (
                    <PeopleMaintenance
                        data={editRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                        parentId={''}
                    />
                )}
                {deleteRecord && (
                    <Delete
                        entity={'corpsphere.people'}
                        data={deleteRecord}
                        setIsOpenDiaglog={setIsOpenMaintenance}
                        isOpenDialog={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />)}
                {selectRecord && (
                    <CompanyPeopleAssociate
                        data={selectRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
            </LayoutDashboard>
        </React.Fragment >
    );
};
